import React, { useState, useEffect } from 'react'; // Added useEffect import for handling side effects
import { useNavigate } from 'react-router-dom';
import Feedback from '../Assets/Feedback/Feedback.jpg';

const FeedbackForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, num3: 0, answer: "" });
  const [captchaError, setCaptchaError] = useState(""); // For handling captcha error message
  const [isLoading, setIsLoading] = useState(false); // Add loading state to handle button states

  const navigate = useNavigate();

  // Generate new CAPTCHA when the component mounts
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Function to generate random CAPTCHA numbers
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1; // Random number 1-10
    const num2 = Math.floor(Math.random() * 10) - 1; // Random number 1-10
    const num3 = Math.floor(Math.random() * 10) * 1; // Random number 1-10
    setCaptcha({ num1, num2, answer: "" });
  };

  // Handle CAPTCHA input change
  const handleCaptchaChange = (e) => {
    setCaptcha({ ...captcha, answer: e.target.value });
    setCaptchaError(""); // Clear any previous error message
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Set loading to true on submit

    const feedbackData = { name, email, message };
    
    // Check if the CAPTCHA answer is correct
    if (parseInt(captcha.answer) !== captcha.num1 + captcha.num2) {
      setCaptchaError("Incorrect CAPTCHA. Please try again.");
      setIsLoading(false); // Reset loading state
      generateCaptcha(); // Generate a new CAPTCHA on error
      return;
    }

    // If CAPTCHA is correct, submit feedback data
    try {
      const response = await fetch('http://127.0.0.1:8000/feedback/submit/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log(data.message);
        setSubmitted(true); // Update the state to show thank you message
      } else {
        const errorData = await response.json();
        console.error('Error:', errorData);
        alert('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false); // Reset loading state after the request completes
    }
  };

  // Close the feedback form and redirect to the home page
  const handleClose = () => {
    navigate('/'); // Redirect to home page
  };

  // Reset the form to allow submitting another response
  const handleSubmitAnotherFeedback = () => {
    setName('');
    setEmail('');
    setMessage('');
    setSubmitted(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="flex flex-col md:flex-row w-full max-w-6xl bg-white rounded shadow-md overflow-hidden">
        {/* Left Image Section */}
        <div className="hidden md:block md:w-1/2 bg-gray-200">
          <img
            src={Feedback}
            alt="Feedback Illustration"
            className="h-full w-full object-cover"
          />
        </div>

        {/* Right Form Section */}
        <div className="relative w-full md:w-1/2 p-10">
          <button
            className="absolute top-2 right-2 text-gray-700 hover:text-gray-900 focus:outline-none text-2xl font-semibold"
            onClick={handleClose}
          >
            &#10005;
          </button>
          <h2 className="text-3xl font-bold text-center mb-8">Feedback Form</h2>
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="name" className="block text-gray-700 font-bold mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="email" className="block text-gray-700 font-bold mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-gray-700 font-bold mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your feedback"
                  rows="5"
                  required
                ></textarea>
              </div>
              {/* CAPTCHA Section */}
              <div>
                <label className="text-gray-800 text-sm mb-2 block font-bold">CAPTCHA</label>
                <div className="flex items-center space-x-2 border border-gray-300 rounded-lg p-3 shadow-sm bg-gray-50">
                  <span className="text-gray-800 font-bold">{`${captcha.num1} + ${captcha.num2} = `}</span>
                  <input
                    type="number"
                    value={captcha.answer}
                    onChange={handleCaptchaChange}
                    className="w-16 text-center text-sm text-gray-800 border border-gray-300 px-2 py-2 rounded-lg outline-none focus:ring-2 focus:ring-[#0077af]"
                    placeholder="?"
                    required
                  />
                </div>
                {captchaError && <p className="text-red-600 text-sm mt-1">{captchaError}</p>} {/* Show CAPTCHA error */}
              </div>
              <button
                type="submit"
                disabled={isLoading} // Disable button while loading
                className="w-full bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 transition duration-200"
              >
                {isLoading ? "Submitting..." : "Submit"} {/* Display loading text */}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-4">Thank you for your feedback!</h3>
              <button
                className="bg-green-500 text-white py-3 px-4 rounded-md hover:bg-green-600 transition duration-200"
                onClick={handleSubmitAnotherFeedback}
              >
                Submit Another Response
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
